import request from '@/utils/request'
import { getToken } from '@/utils/auth'
import store from '@/store'
import { hasPurchasePriceAccess } from '@/utils/purchase-price-access'

// 采购工作台统计
export function getPurchaseWorkbench(data) {
  return request({
    url: '/crmPurchase/workbench',
    method: 'post',
    data: data
  })
}

// 查询采购申请列表
export function listPurchaseApply(data) {
  return request({
    url: '/crmPurchase/apply/list',
    method: 'post',
    data: data
  })
}

// 查询采购申请可关联的合同（仅租户隔离，不受销售合同负责人/部门数据权限限制）
export function listPurchaseContractOptions(keyword) {
  return request({
    url: '/crmPurchase/apply/contract/options',
    method: 'get',
    params: { keyword: keyword || '' }
  })
}

// 查询采购申请详情
export function getPurchaseApplyDetail(id) {
  return request({
    url: `/crmPurchase/apply/detail/${id}`,
    method: 'get'
  })
}

// 保存采购申请
export function savePurchaseApply(data) {
  return request({
    url: '/crmPurchase/apply/save',
    method: 'post',
    data: data
  })
}

// AI识别复杂采购需求清单，仅返回原表及结构化预览，不写入正式采购数据
export function parsePurchaseApplyWorkbook(file) {
  const formData = new FormData()
  formData.append('file', file)
  return request({
    url: '/crmPurchase/apply/workbook/parse',
    method: 'post',
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
    data: formData
  })
}

// 保存需求清单原文件，确认填入草稿时作为采购申请附件归档
export function uploadPurchaseApplySourceFile(file) {
  const formData = new FormData()
  formData.append('file', file)
  return request({
    url: '/upload/',
    method: 'post',
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
    data: formData
  })
}

// 零星采购直接生成供应商采购单
export function saveDirectPurchaseOrder(data) {
  return request({
    url: '/crmPurchase/order/direct/save',
    method: 'post',
    data: data
  })
}

// 提交采购申请
export function submitPurchaseApply(id) {
  return request({
    url: `/crmPurchase/apply/submit/${id}`,
    method: 'post'
  })
}

// 撤回采购申请审批并返回待修改状态
export function withdrawPurchaseApply(id) {
  return request({
    url: `/crmPurchase/apply/withdraw/${id}`,
    method: 'post'
  })
}

// 仅申请人删除本人已撤回的采购申请（服务端保留审批审计记录）
export function deleteWithdrawnPurchaseApply(id) {
  return request({
    url: `/crmPurchase/apply/delete-withdrawn/${id}`,
    method: 'post'
  })
}

export function deleteDraftPurchaseApply(id) {
  return request({
    url: `/crmPurchase/apply/delete-draft/${id}`,
    method: 'post'
  })
}

// 采购专员在总经理未定商前撤回报价修改
export function recallPurchaseSupplierDecision(id) {
  return request({
    url: `/crmPurchase/apply/supplier-decision/recall/${id}`,
    method: 'post'
  })
}

// 生成采购专员可预览的定商分析
export function generatePurchaseSupplierDecisionAnalysis(id) {
  return request({
    url: `/crmPurchase/apply/decision-analysis/${id}`,
    method: 'post',
    timeout: 120000
  })
}

// 采购专员修改报价后重新提交总经理定商
export function resubmitPurchaseSupplierDecision(id) {
  return request({
    url: `/crmPurchase/apply/supplier-decision/resubmit/${id}`,
    method: 'post'
  })
}

// 作废采购申请
export function cancelPurchaseApply(id) {
  return request({
    url: `/crmPurchase/apply/cancel/${id}`,
    method: 'post'
  })
}

// 合同AI识别
export function recognizePurchaseContract(data) {
  return request({
    url: '/crmPurchase/contract/recognize',
    method: 'post',
    timeout: 120000,
    data: data
  })
}

// 合同AI识别进度流
export async function recognizePurchaseContractStream(data, handlers = {}) {
  return recognizePurchaseContractStreamByUrl('/crmPurchase/contract/recognize/stream', data, handlers)
}

// 未保存采购单合同AI识别进度流，仅返回识别草稿，不落库
export async function recognizePurchaseContractDraftStream(data, handlers = {}) {
  return recognizePurchaseContractStreamByUrl('/crmPurchase/contract/recognize/draft/stream', data, handlers)
}

async function recognizePurchaseContractStreamByUrl(url, data, handlers = {}) {
  const response = await fetch(`${process.env.VUE_APP_BASE_API}${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json;charset=utf-8',
      'Terminal-Type': 'PC',
      'Authorization': 'Bearer ' + getToken()
    },
    body: JSON.stringify(data || {})
  })
  if (!response.ok || !response.body) {
    throw new Error(response.status === 401 ? '登录状态已过期' : '合同识别请求异常')
  }
  const reader = response.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  let donePayload = null
  while (true) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const frames = buffer.split('\n\n')
    buffer = frames.pop()
    handleSseFrames(frames, handlers, payload => { donePayload = payload })
  }
  if (buffer) {
    handleSseFrames([buffer], handlers, payload => { donePayload = payload })
  }
  if (!donePayload) {
    throw new Error('合同识别未返回完成结果')
  }
  return donePayload
}

function handleSseFrames(frames, handlers, onDone) {
  for (const frame of frames) {
    const event = parseSseFrame(frame)
    if (!event) continue
    if (event.event === 'progress' && handlers.onProgress) {
      handlers.onProgress(event.data)
    } else if (event.event === 'done') {
      onDone(event.data)
    } else if (event.event === 'error') {
      throw new Error(event.data && event.data.message ? event.data.message : '合同识别失败')
    }
  }
}

function parseSseFrame(frame) {
  if (!frame) return null
  const lines = frame.split('\n')
  let event = 'message'
  const dataLines = []
  lines.forEach(line => {
    if (line.indexOf('event:') === 0) {
      event = line.replace('event:', '').trim()
    } else if (line.indexOf('data:') === 0) {
      dataLines.push(line.replace('data:', '').trim())
    }
  })
  if (!dataLines.length) return null
  return {
    event,
    data: JSON.parse(dataLines.join('\n'))
  }
}

// 保存合同识别回填结果
export function confirmPurchaseContract(data) {
  return request({
    url: '/crmPurchase/contract/confirm',
    method: 'post',
    data: data
  })
}

// 在采购申请主单中按供应商批量保存多份合同
export function batchSavePurchaseContracts(data) {
  return request({
    url: '/crmPurchase/contract/batch/save',
    method: 'post',
    timeout: 120000,
    data: data
  })
}

// 将采购申请对应的全部合同一次性提交用章审批
export function submitPurchaseContractSealBatch(data) {
  return request({
    url: '/crmPurchase/contract/seal/submit',
    method: 'post',
    data: data
  })
}

// 将当前采购申请的全部合同附件打包为 ZIP 下载
export function downloadPurchaseContractSealFiles(applyId) {
  return request({
    url: `/crmPurchase/contract/seal/files/download/${applyId}`,
    method: 'get',
    responseType: 'blob',
    timeout: 120000
  })
}

// 设置采购合同模式
export function updatePurchaseContractMode(data) {
  return request({
    url: '/crmPurchase/contract/mode',
    method: 'post',
    data: data
  })
}

// 查询询价比价
export function listPurchaseQuote(applyId) {
  return request({
    url: `/crmPurchase/quote/list/${applyId}`,
    method: 'post'
  })
}

// 保存询价比价
export function savePurchaseQuote(data) {
  return request({
    url: '/crmPurchase/quote/save',
    method: 'post',
    data: data
  })
}

// 批量保存询价比价
export function savePurchaseQuoteBatch(data) {
  return request({
    url: '/crmPurchase/quote/batchSave',
    method: 'post',
    data: data
  })
}

// 按真实采购项保存多供应商报价组
export function savePurchaseItemQuoteGroup(data) {
  return request({
    url: '/crmPurchase/quote/item-group/save',
    method: 'post',
    data: data
  })
}

// 推荐或取消推荐采购项中的一家供应商
export function recommendPurchaseItemQuote(data) {
  return request({
    url: '/crmPurchase/quote/item-group/recommend',
    method: 'post',
    data: data
  })
}

// 删除采购项报价组
export function removePurchaseItemQuoteGroup(data) {
  return request({
    url: '/crmPurchase/quote/item-group/remove',
    method: 'post',
    data: data
  })
}

// 保存多采购项、多供应商报价组
export function savePurchaseComparisonGroup(data) {
  return request({
    url: '/crmPurchase/quote/comparison-group/save',
    method: 'post',
    data: data
  })
}

// 推荐或取消推荐报价组中的一家供应商
export function recommendPurchaseComparisonSupplier(data) {
  return request({
    url: '/crmPurchase/quote/comparison-group/recommend',
    method: 'post',
    data: data
  })
}

// 删除整个报价组
export function removePurchaseComparisonGroup(data) {
  return request({
    url: '/crmPurchase/quote/comparison-group/remove',
    method: 'post',
    data: data
  })
}

// 解析旧版供应商横向报价表
export function parsePurchaseQuoteWorkbook(file) {
  const formData = new FormData()
  formData.append('file', file)
  return request({
    url: '/crmPurchase/quote/workbook/parse',
    method: 'post',
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
    data: formData
  })
}

// 确认导入解析后的供应商报价表
export function savePurchaseQuoteWorkbook(data) {
  return request({
    url: '/crmPurchase/quote/workbook/save',
    method: 'post',
    timeout: 120000,
    data: data
  })
}

// 采购员在询价节点选择正式供应商询价、电商平台店铺比价或混合报价
export function updatePurchaseMode(data) {
  return request({
    url: '/crmPurchase/quote/purchase-mode',
    method: 'post',
    data
  })
}

// 删除询价比价
export function removePurchaseQuote(id) {
  return request({
    url: `/crmPurchase/quote/remove/${id}`,
    method: 'post'
  })
}

// 推荐或取消推荐整组供应商报价
export function recommendPurchaseQuoteGroup(data) {
  return request({
    url: '/crmPurchase/quote/group/recommend',
    method: 'post',
    data: data
  })
}

// 删除整组供应商报价
export function removePurchaseQuoteGroup(data) {
  return request({
    url: '/crmPurchase/quote/group/remove',
    method: 'post',
    data: data
  })
}

// 查询付款计划
export function listPurchasePaymentPlan(applyId) {
  return request({
    url: `/crmPurchase/payment/list/${applyId}`,
    method: 'post'
  })
}

// 登记付款
export function registerPurchasePayment(data) {
  return request({
    url: '/crmPurchase/payment/register',
    method: 'post',
    data: data
  })
}

// 删除付款记录
export function removePurchasePayment(id) {
  return request({
    url: `/crmPurchase/payment/remove/${id}`,
    method: 'post'
  })
}

// 补录付款节点发票
export function savePurchasePaymentInvoice(data) {
  return request({
    url: '/crmPurchase/payment/invoice/save',
    method: 'post',
    data: data
  })
}

// 查询到货验收
export function listPurchaseArrival(applyId) {
  return request({
    url: `/crmPurchase/arrival/list/${applyId}`,
    method: 'post'
  })
}

// 保存到货验收
export function savePurchaseArrival(data) {
  return request({
    url: '/crmPurchase/arrival/save',
    method: 'post',
    data: data
  })
}

// 查询发货物流记录
export function listPurchaseShipment(applyId) {
  return request({
    url: `/crmPurchase/shipment/list/${applyId}`,
    method: 'post'
  })
}

// 保存发货物流记录
export function savePurchaseShipment(data) {
  return request({
    url: '/crmPurchase/shipment/save',
    method: 'post',
    data: data
  })
}

// 删除发货物流记录
export function removePurchaseShipment(id) {
  return request({
    url: `/crmPurchase/shipment/remove/${id}`,
    method: 'post'
  })
}

// 查询供应商列表
export function listPurchaseSupplier(data) {
  return request({
    url: '/crmPurchase/supplier/list',
    method: 'post',
    data: data
  })
}

// 保存供应商
export function savePurchaseSupplier(data) {
  return request({
    url: '/crmPurchase/supplier/save',
    method: 'post',
    data: data
  })
}

// 删除供应商
export function removePurchaseSupplier(id) {
  return request({
    url: `/crmPurchase/supplier/remove/${id}`,
    method: 'post'
  })
}

// 查询价格库
export function listPurchasePrice(data) {
  if (!hasPurchasePriceAccess(store.getters.roles)) return Promise.reject({ code: 403, msg: '暂无价格库权限' })
  return request({
    url: '/crmPurchase/price/list',
    method: 'post',
    data: data
  })
}

export const purchaseApprovalPreview = data => request({url:"/crmPurchase/apply/approval-preview",method:"post",data})
